import type { Slideshow, SlideshowStatus } from '@/types/api'

export const STATUS_FILTERS: SlideshowStatus[] = ['draft', 'pending_approval', 'approved', 'published']

export const STATUS_LABELS: Record<SlideshowStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  published: 'Published',
  archived: 'Archived',
}

export type StatusTone = 'secondary' | 'warning' | 'default' | 'success' | 'offline'

export const STATUS_TONES: Record<SlideshowStatus, StatusTone> = {
  draft: 'secondary',
  pending_approval: 'warning',
  approved: 'default',
  published: 'success',
  archived: 'offline',
}

export function statusLabel(status: string): string {
  return STATUS_LABELS[status as SlideshowStatus] ?? status
}

export const SLIDESHOW_SORTS = ['-updated_at', 'name', '-name', '-created_at'] as const
export type SlideshowSort = (typeof SLIDESHOW_SORTS)[number]

export const SORT_LABELS: Record<SlideshowSort, string> = {
  '-updated_at': 'Recently updated',
  name: 'Name (A–Z)',
  '-name': 'Name (Z–A)',
  '-created_at': 'Newest first',
}

/** The lock is someone else's and still active. */
export function lockedByOther(s: Pick<Slideshow, 'lock'>, now: Date = new Date()): boolean {
  return !!s.lock && !s.lock.is_mine && new Date(s.lock.expires_at).getTime() > now.getTime()
}

export interface WorkflowActions {
  submit: boolean
  approve: boolean
  reject: boolean
  publish: boolean
  /** Why publishing is blocked (approval required). */
  publishBlockedReason: string | null
}

/**
 * Which workflow buttons to offer. Approval actions only exist when the tenant requires
 * approval (`requires_approval`); publishing then needs an approved draft.
 */
export function workflowActions(
  s: Pick<Slideshow, 'status' | 'requires_approval' | 'has_unpublished_changes' | 'published_version'>,
  perms: { edit: boolean; approve: boolean; publish: boolean },
): WorkflowActions {
  const changes = s.has_unpublished_changes || !s.published_version
  const approval = s.requires_approval
  const blocked = approval && s.status !== 'approved' ? (s.status === 'pending_approval' ? 'Waiting for approval' : 'Submit for approval first') : null
  return {
    submit: approval && perms.edit && changes && s.status === 'draft',
    approve: approval && perms.approve && s.status === 'pending_approval',
    reject: approval && perms.approve && s.status === 'pending_approval',
    publish: perms.publish && changes,
    publishBlockedReason: changes ? blocked : null,
  }
}
